import { useEffect } from 'react';
import { BindingType } from '../types';

interface KeyboardShortcutCallbacks {
  /** Called when spacebar is pressed (capture) */
  onCapture?: () => void;
  /** Called when binding type changes via keyboard */
  onBindingTypeChange?: (type: BindingType) => void;
}

interface KeyboardShortcutConditions {
  /** Whether capture is allowed (e.g., webcam tab active, not loading) */
  canCapture?: boolean;
  /** Current tab index (0 = webcam) */
  tabIndex?: number;
  /** Whether currently loading */
  isLoading?: boolean;
}

/**
 * Custom hook for handling keyboard shortcuts.
 *
 * Shortcuts:
 * - Space: Capture image (when webcam tab active and not loading)
 * - P: Set binding type to paperback
 * - H: Set binding type to hardcover
 * - B: Set binding type to barcode
 */
export function useKeyboardShortcuts(
  callbacks: KeyboardShortcutCallbacks,
  conditions: KeyboardShortcutConditions = {}
) {
  const { onCapture, onBindingTypeChange } = callbacks;
  const { canCapture = true, tabIndex = 0, isLoading = false } = conditions;

  // Handle spacebar capture
  useEffect(() => {
    if (!onCapture) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      // Only respond if webcam tab is active and not loading
      if (tabIndex === 0 && canCapture && !isLoading && e.code === 'Space') {
        e.preventDefault(); // Prevent page scrolling
        onCapture();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [tabIndex, canCapture, isLoading, onCapture]);

  // Handle binding type shortcuts
  useEffect(() => {
    if (!onBindingTypeChange) return;

    const handleBindingKeyPress = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input field
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      switch (e.key.toLowerCase()) {
        case 'p':
          onBindingTypeChange('paperback');
          console.log('Binding type set to Paperback via shortcut');
          break;
        case 'h':
          onBindingTypeChange('hardcover');
          console.log('Binding type set to Hardcover via shortcut');
          break;
        case 'b':
          onBindingTypeChange('barcode');
          console.log('Binding type set to Barcode via shortcut');
          break;
      }
    };

    document.addEventListener('keydown', handleBindingKeyPress);
    return () => document.removeEventListener('keydown', handleBindingKeyPress);
  }, [onBindingTypeChange]);
}

/**
 * Hook that provides keyboard shortcut info for display.
 */
export function useKeyboardShortcutInfo() {
  return {
    shortcuts: [
      { key: 'Space', action: 'Capture image', scope: 'Webcam tab' },
      { key: 'P', action: 'Set to Paperback', scope: 'Global' },
      { key: 'H', action: 'Set to Hardcover', scope: 'Global' },
      { key: 'B', action: 'Set to Barcode', scope: 'Global' },
    ],
  };
}
